import { useEffect, useRef, useState } from 'react';
import { refundsApi } from '../../services/refundsApi';
import {
  CommissionTreatment,
  FaultParty,
  FeeBorneBy,
  RefundPreviewResponse,
  feeBorneByPhrase,
  serviceFeeBorneByPhrase,
} from '../../types/refunds';

const PREVIEW_DEBOUNCE_MS = 300;

/**
 * Spec 053 Flow B step 2 / AC-7, extended for Phase 3 Flow F5.
 *
 * <p>This is the delivery-fee-aware consequence line for the order-detail refund and dispute
 * dialogs. It supersedes `components/finance/ConsequenceLine` for those two callers: the split the
 * admin has to see now has a second axis (the garage's service vs Garagat's own pickup & delivery
 * supply), and which documents get issued follows from that split. That component is still the one
 * the finance surfaces use and is left untouched here — folding the two together belongs to the
 * slice that owns `components/finance/`.
 *
 * <p>The fetch lives in {@link useRefundPreview} rather than in the component so the dialogs can
 * read the preview themselves — the fee checkbox has to know whether a fee was charged even when
 * the order payload predates the field.
 */

export interface RefundPreviewInput {
  /** Existing refund to preview (approval path). */
  refundId?: string | null;
  /** Payment to preview when no refund row exists yet (order-detail path). */
  paymentId?: string | null;
  faultParty: FaultParty | null;
  /** The SERVICE amount; null → let the backend default to the ceiling (full refund). */
  amount: number | null;
  /**
   * false → the caller still needs an amount (partial refund with an empty/invalid field): no
   * preview call is made and a hint is shown instead of a full-refund projection.
   */
  enabled?: boolean;
  /** #1059 — undefined = billing's default (Garagat for its own fault and system refunds); set = the admin's choice. */
  feeBorneBy?: FeeBorneBy;
  commissionTreatment?: CommissionTreatment;
  /** BR-25 — preview the fee reversal too. */
  refundDeliveryFee?: boolean;
  /** Spec 068 — undefined = the policy default for the fault party; true/false = the admin's choice. */
  refundServiceFee?: boolean;
  /** #716 — part of a dispute close (completes the order first). */
  closing?: boolean;
  token: string;
}

export interface RefundPreviewState {
  preview: RefundPreviewResponse | null;
  loading: boolean;
  error: string | null;
  /**
   * true while a preview is due for the CURRENT inputs but has not answered yet (debounce or in
   * flight). Unlike `loading` it is true on the very render the inputs change, before the effect
   * runs, so a dialog can never act on the previous fault party's preview. Optional so callers that
   * build a state by hand (tests) need not set it.
   */
  pending?: boolean;
}

export function useRefundPreview({
  refundId,
  paymentId,
  faultParty,
  amount,
  enabled = true,
  feeBorneBy,
  commissionTreatment,
  refundDeliveryFee,
  refundServiceFee,
  closing,
  token,
}: RefundPreviewInput): RefundPreviewState {
  const [preview, setPreview] = useState<RefundPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sequence guard: a slow earlier response must never overwrite a newer one.
  const seq = useRef(0);
  // Which inputs the shown preview (or error) answered — `pending` compares it with the current ones.
  const key = JSON.stringify([
    enabled, refundId, paymentId, faultParty, amount, feeBorneBy, commissionTreatment,
    refundDeliveryFee, refundServiceFee, closing,
  ]);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const active = enabled && !!faultParty && !!(refundId || paymentId) && !!token;

  useEffect(() => {
    if (!enabled || !faultParty || (!refundId && !paymentId) || !token) {
      setPreview(null);
      setError(null);
      setLoading(false);
      return;
    }
    const mySeq = ++seq.current;
    setLoading(true);
    const timer = setTimeout(() => {
      const params = {
        faultParty,
        amount: amount ?? undefined,
        feeBorneBy,
        commissionTreatment,
        refundDeliveryFee,
        refundServiceFee,
        closing,
      };
      const call = refundId
        ? refundsApi.preview(refundId, params, token)
        : refundsApi.previewByPayment(paymentId as string, params, token);
      call
        .then((res) => {
          if (seq.current !== mySeq) return;
          setPreview(res);
          setError(null);
          setSettledKey(key);
        })
        .catch((err) => {
          if (seq.current !== mySeq) return;
          setPreview(null);
          setError(err instanceof Error ? err.message : 'Preview unavailable');
          // An error settles the inputs too: a billing outage must not lock the dialog's submit.
          setSettledKey(key);
        })
        .finally(() => {
          if (seq.current === mySeq) setLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    enabled,
    refundId,
    paymentId,
    faultParty,
    amount,
    feeBorneBy,
    commissionTreatment,
    refundDeliveryFee,
    refundServiceFee,
    closing,
    token,
    key,
  ]);

  return { preview, loading, error, pending: active && settledKey !== key };
}

function Money({ amount, currency }: { amount: number; currency: string }) {
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
      {currency} {amount.toFixed(2)}
    </span>
  );
}

interface RefundConsequenceProps extends RefundPreviewState {
  faultParty: FaultParty | null;
  enabled?: boolean;
  currency: string;
  garageName: string | null;
}

/**
 * Purely informational: it never blocks the submit (Flow D2 step 3 — approval is allowed on a
 * negative projection). Amounts come from the preview, never from local arithmetic, so what the
 * admin reads is what billing will do.
 */
export default function RefundConsequence({
  preview,
  loading,
  error,
  faultParty,
  enabled = true,
  currency,
  garageName,
}: RefundConsequenceProps) {
  const muted = { fontSize: '0.8125rem', color: 'var(--admin-text-muted)' } as const;

  if (!faultParty) {
    return (
      <p data-testid="consequence-line" style={muted}>
        Select who is responsible to see what this refund will do.
      </p>
    );
  }
  if (!enabled) {
    return <p data-testid="consequence-line" style={muted}>Enter an amount to preview</p>;
  }
  if (loading && !preview) {
    return <p data-testid="consequence-line" style={muted}>Calculating consequences...</p>;
  }
  if (error) {
    return <p data-testid="consequence-line" style={muted}>Preview unavailable: {error}</p>;
  }
  if (!preview) return null;

  const feeAmount = preview.feeAmount;
  const commissionReversed = preview.commissionTreatment === 'REVERSE_PROPORTIONAL' && preview.commissionReversed > 0;
  const negativeAmount = Math.abs(preview.garagePositionAfter ?? 0);

  // Phase 3: a fee of 0 (or a billing build that predates the fields) renders exactly as before.
  const feeCharged = preview.deliveryFeeCharged ?? 0;
  const feeRefunded = preview.deliveryFeeRefunded ?? 0;
  const serviceRefund = preview.amountRequested;
  // Spec 068 — Garagat's service fee: 0.00/null/absent renders nothing (V11).
  const svcFeeCharged = preview.serviceFeeCharged ?? 0;
  const svcFeeRefunded = preview.serviceFeeRefunded ?? 0;
  const svcFeeBearer = serviceFeeBorneByPhrase(preview.serviceFeeBorneBy);
  const svcFeeRecharge = preview.serviceFeeRecharge ?? 0;

  return (
    <div data-testid="consequence-line" style={{ fontSize: '0.875rem', lineHeight: 1.6, opacity: loading ? 0.6 : 1 }}>
      {feeCharged > 0 && (
        <p style={{ margin: '0 0 0.25rem' }} data-testid="consequence-split">
          Service (garage) <Money amount={serviceRefund} currency={currency} />
          {' · '}Pickup &amp; delivery (Garagat){' '}
          {feeRefunded > 0 ? (
            <>
              <Money amount={feeRefunded} currency={currency} /> refunded
            </>
          ) : (
            <>
              <Money amount={feeCharged} currency={currency} /> kept
            </>
          )}
        </p>
      )}
      {svcFeeCharged > 0 && (
        <p style={{ margin: '0 0 0.25rem' }} data-testid="consequence-service-fee">
          {svcFeeRefunded > 0 ? (
            <>
              Service fee refunded <Money amount={svcFeeRefunded} currency={currency} />
              {svcFeeBearer && <>, {svcFeeBearer}</>}
              {preview.serviceFeeBorneBy === 'GARAGE' && svcFeeRecharge > 0 && (
                <> (Service fee recharge <Money amount={svcFeeRecharge} currency={currency} /> at settlement)</>
              )}
            </>
          ) : (
            <>
              Service fee (Garagat) <Money amount={svcFeeCharged} currency={currency} /> kept
            </>
          )}
        </p>
      )}
      <p style={{ margin: 0 }}>
        Customer receives <Money amount={preview.customerReceives} currency={currency} />
        {feeAmount != null && feeAmount > 0 && (
          <>
            {' '}(processing fee <Money amount={feeAmount} currency={currency} />{' '}
            {feeBorneByPhrase(preview.feeBorneBy)}
            {!preview.feeIsActual && ', estimated'})
          </>
        )}
        {preview.paidFromHeldFunds ? (
          <>
            {preview.platformExpense > 0 && (
              <>{' · '}Garagat bears <Money amount={preview.platformExpense} currency={currency} /></>
            )}
          </>
        ) : (
          <>
        {' · '}Garage bears <Money amount={preview.garageBears} currency={currency} />
        {' · '}Garagat bears <Money amount={preview.platformExpense} currency={currency} />
        {' · '}
        {commissionReversed ? (
          <>
            Commission reversed <Money amount={preview.commissionReversed} currency={currency} /> + VAT{' '}
            <Money amount={preview.vatOnCommissionReversed} currency={currency} />
          </>
        ) : (
          'Commission retained'
        )}
          </>
        )}
      </p>
      {preview.paidFromHeldFunds && (
        <p style={{ margin: '0.25rem 0 0' }} data-testid="consequence-held-funds">
          Paid back from the money Garagat is holding for this order. It has not completed, so{' '}
          {garageName ?? 'the garage'} is not charged and no commission moves.
        </p>
      )}
      {(serviceRefund > 0 || feeRefunded > 0 || svcFeeRefunded > 0) && (
        <p style={{ ...muted, margin: '0.25rem 0 0' }} data-testid="consequence-documents">
          Documents:{' '}
          {[
            serviceRefund > 0 ? "credit note against the garage's invoice (GCN)" : null,
            // One PCN carries both Garagat lines (delivery and service fee), Spec 068 DD6.
            feeRefunded > 0 || svcFeeRefunded > 0 ? "credit note against Garagat's invoice (PCN)" : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {preview.projectedNegative && (
        <div className="admin-info-banner warning" role="alert" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          Projected balance for {garageName ?? 'this garage'} after this refund:{' '}
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            −{currency} {negativeAmount.toFixed(2)}
          </span>
          , carried to following cycles until covered
        </div>
      )}
      {preview.warning && (
        <p style={{ ...muted, marginTop: '0.5rem', marginBottom: 0 }}>{preview.warning}</p>
      )}
    </div>
  );
}
